


import React, { useState, useEffect, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Tooltip,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Block as BlockIcon,
  CheckCircle as UnblockIcon,
  Phone as PhoneIcon,
  DeviceUnknown as DeviceIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
  
} from '@mui/icons-material';
import { format } from 'date-fns';
import DataTable from '../components/common/DataTable';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { adminSubscriberService } from '../services/adminSubscriber';
import { adminService } from '../services/admin';
import { useAuth } from '../context/AuthContext';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
// import ContentCopyIcon from '@mui/icons-material/ContentCopy'; // only used by the commented-out Device ID / Subscriber ID columns below
// Status chip colors
const STATUS_COLORS = {
  'Blocked': 'error',
  'Active': 'success'
};

const SMS_STATUS_COLORS = {
  'Verified': 'success',
  'Not Verified': 'warning'
};

// 'onboardedOn' arrives pre-formatted from the API as en-IN locale text
// (D/M/YYYY, no leading zeros) rather than a raw timestamp, so a plain
// string sort would order it lexically instead of chronologically. Parse
// it back into a Date so the column header sorts by actual date.
const parseOnboardedDate = (value) => {
  if (!value || value === 'N/A') return null;
  const [day, month, year] = value.split('/').map(Number);
  if (!day || !month || !year) return null;
  return new Date(year, month - 1, day).getTime();
};

const AdminSubscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(''); // Actual search term used for API
  const [searchInput, setSearchInput] = useState(''); // Local input state
  const [filterBlocked, setFilterBlocked] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [stats, setStats] = useState({ total: 0, blocked: 0, active: 0, verified: 0 });

  // Block dialog state
  const [blockDialog, setBlockDialog] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [blocking, setBlocking] = useState(false);

  // Unblock dialog state
  const [unblockDialog, setUnblockDialog] = useState(null);
  const [unblocking, setUnblocking] = useState(false);

  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const searchInputRef = useRef(null);

  const [unblockReason, setUnblockReason] = useState('');

  const { isAdmin, isBusiness, user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // businessIdFilter holds the businessId (tenantId) sent to the API; the
  // dropdown itself displays business NAMES — see businessOptions below.
  const [businessIdFilter, setBusinessIdFilter] = useState('');
  const [businessOptions, setBusinessOptions] = useState([]);
  const [businessOptionsLoading, setBusinessOptionsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0); // ✅ add this

  useEffect(() => {
    if (!isAdmin) return undefined; // dropdown only shown/used for admins
    let cancelled = false;
    (async () => {
      try {
        setBusinessOptionsLoading(true);
        // Large limit so every business shows up in the dropdown, not just page 1.
        const res = await adminService.getAllBusinesses(0, 1000, '', '');
        const list = res?.data || res?.businesses || [];
        if (!cancelled) {
          setBusinessOptions(
            list
              .map((b) => ({ businessId: b.businessId || b.tenantId, businessName: b.businessName }))
              .filter((b) => b.businessId && b.businessName)
              .sort((a, b) => a.businessName.localeCompare(b.businessName))
          );
        }
      } catch (error) {
        console.error('Failed to fetch businesses for filter dropdown:', error);
      } finally {
        if (!cancelled) setBusinessOptionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  const handleBusinessIdChange = (e) => {
    setBusinessIdFilter(e.target.value);
    setPage(0);
  };

  const handleClearBusinessId = () => {
    setBusinessIdFilter('');
    setPage(0);
  };

  const selectedBusinessName = businessOptions.find((b) => b.businessId === businessIdFilter)?.businessName || '';

  // Handle search button click
  const handleSearchClick = () => {
    setSearchTerm(searchInput);
    setPage(0); // Reset to first page on new search
  };

  // Handle enter key press in search field
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(0);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };  

  // Fetch subscribers
  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      
      const response = await adminSubscriberService.getAllSubscribers(
        page + 1,
        rowsPerPage,
        filterBlocked,
        searchTerm,
        businessIdFilter  // Use searchTerm instead of search
      );
      
      setSubscribers(response.data || []);
      setTotalRows(response.pagination?.total || 0);
      setStats(response.stats || { total: 0, blocked: 0, active: 0, verified: 0 });
    } catch (error) {
      console.error('Fetch error:', error);
      setSubscribers([]);
      setTotalRows(0);
      setStats({ total: 0, blocked: 0, active: 0, verified: 0 });
      showSnackbar('Failed to fetch subscribers', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
  console.log('📊 Subscribers updated:', subscribers.length, subscribers);
}, [subscribers]);

  useEffect(() => {
    fetchSubscribers();
  }, [page, rowsPerPage, searchTerm, filterBlocked,businessIdFilter]); // Depend on searchTerm instead of search

  // Handle block subscriber
  const handleBlock = async () => {
    if (!blockReason.trim()) {
      showSnackbar('Blocking reason is required', 'error');
      return;
    }

    try {
      setBlocking(true);
      await adminSubscriberService.blockSubscriber(blockDialog._id, blockReason);
      showSnackbar(`Subscriber ${blockDialog.phoneNumber} blocked successfully`, 'success');
      setBlockDialog(null);
      setBlockReason('');
      fetchSubscribers();
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to block subscriber', 'error');
    } finally {
      setBlocking(false);
    }
  };

  // Handle unblock subscriber
  const handleUnblock = async () => {
      if (!unblockReason.trim()) {
    showSnackbar('Unblocking reason is required', 'error');
    return;
    }
    try {
      setUnblocking(true);
      // await adminSubscriberService.unblockSubscriber(unblockDialog._id);
       await adminSubscriberService.unblockSubscriber(unblockDialog._id, unblockReason);
      showSnackbar(`Subscriber ${unblockDialog.phoneNumber} unblocked successfully`, 'success');
      setUnblockDialog(null);
      fetchSubscribers();
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to unblock subscriber', 'error');
    } finally {
      setUnblocking(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
const columns = isAdmin
  ? [
      { field: 'srNo', headerName: 'Sr No', width: 70, align: 'center' },
      {
        field: 'phoneNumber',
        headerName: 'Phone Number',
        width: 150,
        render: (value) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 500, color: theme.palette.text.primary }}>{value}</Typography>
          </Box>
        ),
      },
      /*
      {
        field: 'deviceId',
        headerName: 'Device ID',
        width: 180,
        render: (value) => {
          const fullId = value || 'N/A';
          const displayId = fullId.length > 12 ? `${fullId.substring(0, 10)}…` : fullId;
          const handleCopy = () => {
            if (fullId && fullId !== 'N/A') {
              navigator.clipboard.writeText(fullId).catch(err => console.error('Copy failed:', err));
            }
          };
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={fullId}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer', color: theme.palette.text.primary }} onClick={handleCopy}>{displayId}</Typography>
              </Tooltip>
              <Tooltip title="Copy Device ID">
                <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25, color: theme.palette.text.secondary }}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: '_id',
        headerName: 'Subscriber ID',
        width: 180,
        sortable: true,
        render: (value) => {
          const fullId = value || '';
          const displayId = fullId.length > 12 ? `${fullId.substring(0, 10)}…` : fullId;
          const handleCopy = () => {
            if (fullId) {
              navigator.clipboard.writeText(fullId).catch(err => console.error('Copy failed:', err));
            }
          };
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={fullId}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer', color: theme.palette.text.primary }} onClick={handleCopy}>{displayId}</Typography>
              </Tooltip>
              <Tooltip title="Copy Subscriber ID">
                <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25, color: theme.palette.text.secondary }}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      */
      { field: 'smsVerification', headerName: 'SMS Verification', width: 140, render: (value) => (<Chip label={value} size="small" color={value === 'Verified' ? 'success' : 'warning'} sx={{ fontWeight: 500 }} />) },
      { field: 'emailVerification', headerName: 'Email Verification', width: 140, render: (value) => (<Chip label={value} size="small" color={value === 'Verified' ? 'success' : 'warning'} sx={{ fontWeight: 500 }} />) },
      { field: 'onboardedOn', headerName: 'Onboarded On', width: 120, sortable: true, sortValue: (v) => parseOnboardedDate(v), render: (value) => (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CalendarIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} /><Typography variant="body2" sx={{ color: theme.palette.text.primary }}>{value}</Typography></Box>) },
      { field: 'status', headerName: 'Status', width: 120, render: (value) => { const isBlocked = (value ?? '').includes('Blocked'); return <Chip label={value} size="small" color={isBlocked ? 'error' : 'success'} sx={{ fontWeight: 600 }} />; } },
      { field: 'blockReason', headerName: 'Block Reason', width: 200, render: (value, row) => { if (!row.isBlocked || !value) return '—'; return (<Tooltip title={`Blocked by: ${row.blockedBy?.email || 'Admin'}`}><Typography variant="body2" sx={{ color: theme.palette.error.main, backgroundColor: alpha(theme.palette.error.main, isDark ? 0.18 : 0.12), px: 1, py: 0.5, borderRadius: 1, fontSize: '0.75rem', display: 'inline-block' }}>{value.length > 30 ? `${value.substring(0, 30)}...` : value}</Typography></Tooltip>); } },
      { field: 'actions', headerName: 'Actions', width: 200, align: 'center', render: (_, row) => (<Stack direction="row" spacing={1} justifyContent="center">{row.isBlocked ? <Button size="small" variant="contained" onClick={() => setUnblockDialog(row)} startIcon={<UnblockIcon />} sx={{ backgroundColor: theme.palette.success.main, color: '#fff', textTransform: 'none', fontWeight: 500, fontSize: '0.75rem', py: 0.5, px: 1.5, minWidth: '80px', boxShadow: 'none', '&:hover': { backgroundColor: theme.palette.success.dark } }}>Unblock</Button> : <Button size="small" variant="contained" onClick={() => setBlockDialog(row)} startIcon={<BlockIcon />} sx={{ backgroundColor: theme.palette.error.main, color: '#fff', textTransform: 'none', fontWeight: 500, fontSize: '0.75rem', py: 0.5, px: 1.5, minWidth: '80px', boxShadow: 'none', '&:hover': { backgroundColor: theme.palette.error.dark } }}>Block</Button>}</Stack>) },
    ]
  : isBusiness
  ? [
      { field: 'srNo', headerName: 'Sr No', width: 70, align: 'center' },
      { field: 'phoneNumber', headerName: 'Phone Number', width: 150, render: (value) => (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PhoneIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} /><Typography sx={{ fontFamily: 'monospace', fontWeight: 500, color: theme.palette.text.primary }}>{value}</Typography></Box>) },
      // { field: 'deviceId', headerName: 'Device ID', width: 180, render: (value) => { const fullId = value || 'N/A'; const displayId = fullId.length > 12 ? `${fullId.substring(0, 10)}…` : fullId; const handleCopy = () => { if (fullId && fullId !== 'N/A') { navigator.clipboard.writeText(fullId).catch(err => console.error('Copy failed:', err)); } }; return (<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Tooltip title={fullId}><Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer', color: theme.palette.text.primary }} onClick={handleCopy}>{displayId}</Typography></Tooltip><Tooltip title="Copy Device ID"><IconButton size="small" onClick={handleCopy} sx={{ p: 0.25, color: theme.palette.text.secondary }}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip></Box>); } },
      // { field: '_id', headerName: 'Subscriber ID', width: 180, sortable: true, render: (value) => { const fullId = value || ''; const displayId = fullId.length > 12 ? `${fullId.substring(0, 10)}…` : fullId; const handleCopy = () => { if (fullId) { navigator.clipboard.writeText(fullId).catch(err => console.error('Copy failed:', err)); } }; return (<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Tooltip title={fullId}><Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer', color: theme.palette.text.primary }} onClick={handleCopy}>{displayId}</Typography></Tooltip><Tooltip title="Copy Subscriber ID"><IconButton size="small" onClick={handleCopy} sx={{ p: 0.25, color: theme.palette.text.secondary }}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip></Box>); } },
      { field: 'smsVerification', headerName: 'SMS Verification', width: 140, render: (value) => (<Chip label={value} size="small" color={SMS_STATUS_COLORS[value] || 'default'} sx={{ fontWeight: 500 }} />) },
      { field: 'onboardedOn', headerName: 'Onboarded On', width: 120, sortable: true, sortValue: (v) => parseOnboardedDate(v), render: (value) => (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CalendarIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} /><Typography variant="body2" sx={{ color: theme.palette.text.primary }}>{value}</Typography></Box>) },
      { field: 'businessStatus', headerName: 'Business Status', width: 140, render: (value) => { const isBlocked = (value ?? '').includes('Blocked'); return (<Chip label={value} size="small" color={isBlocked ? 'error' : 'success'} sx={{ fontWeight: 600 }} />); } },
      { field: 'globalStatus', headerName: 'Global Status', width: 140, render: (value) => { const isBlocked = (value ?? '').includes('Blocked'); return (<Chip label={value} size="small" color={isBlocked ? 'error' : 'success'} sx={{ fontWeight: 600 }} />); } },
      { field: 'blockReason', headerName: 'Block Reason', width: 200, render: (value, row) => { if (!row.isBlockedForBusiness || !value) return '—'; return (<Tooltip title={'Blocked for your business'}><Typography variant="body2" sx={{ color: theme.palette.error.main, backgroundColor: alpha(theme.palette.error.main, isDark ? 0.18 : 0.12), px: 1, py: 0.5, borderRadius: 1, fontSize: '0.75rem', display: 'inline-block' }}>{value.length > 30 ? `${value.substring(0, 30)}...` : value}</Typography></Tooltip>); } },
      { field: 'actions', headerName: 'Actions', width: 200, align: 'center', render: (_, row) => (<Stack direction="row" spacing={1} justifyContent="center">{row.isBlockedForBusiness ? <Button size="small" variant="contained" onClick={() => setUnblockDialog(row)} startIcon={<UnblockIcon />} sx={{ backgroundColor: theme.palette.success.main, color: '#fff', textTransform: 'none', fontWeight: 500, fontSize: '0.75rem', py: 0.5, px: 1.5, minWidth: '80px', boxShadow: 'none', '&:hover': { backgroundColor: theme.palette.success.dark } }}>Unblock</Button> : <Button size="small" variant="contained" onClick={() => setBlockDialog(row)} startIcon={<BlockIcon />} sx={{ backgroundColor: theme.palette.error.main, color: '#fff', textTransform: 'none', fontWeight: 500, fontSize: '0.75rem', py: 0.5, px: 1.5, minWidth: '80px', boxShadow: 'none', '&:hover': { backgroundColor: theme.palette.error.dark } }}>Block</Button>}</Stack>) },
    ]
  : [];

const statsCards = isAdmin
  ? [
      { label: 'Total Subscribers', value: stats.total, color: theme.palette.primary.main, bgColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1), icon: <PhoneIcon /> },
      { label: 'Active', value: stats.active, color: theme.palette.success.main, bgColor: alpha(theme.palette.success.main, isDark ? 0.18 : 0.12), icon: <UnblockIcon /> },
      { label: 'Blocked', value: stats.blocked, color: theme.palette.error.main, bgColor: alpha(theme.palette.error.main, isDark ? 0.18 : 0.12), icon: <BlockIcon /> },
    ]
  : isBusiness
    ? [
        { label: 'Total Linked', value: stats.totalLinked, color: theme.palette.primary.main, bgColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1), icon: <PhoneIcon /> },
        { label: 'Active for Business', value: stats.activeForBusiness, color: theme.palette.success.main, bgColor: alpha(theme.palette.success.main, isDark ? 0.18 : 0.12), icon: <UnblockIcon /> },
        { label: 'Blocked for Business', value: stats.blockedForBusiness, color: theme.palette.error.main, bgColor: alpha(theme.palette.error.main, isDark ? 0.18 : 0.12), icon: <BlockIcon /> }
      ]
    : [];

  if (loading && page === 0 && subscribers.length === 0) return <LoadingSpinner />;

  return (
    <Box sx={{ minHeight: '100%', background: theme.palette.background.default, p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 1 }}>
          {isAdmin ? 'Subscribers Management' : 'My Business Subscribers'}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {isAdmin ? 'Manage authenticator app users, block/unblock devices, and view verification status' : 'View and manage subscribers linked to your business'}
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statsCards.map((stat) => (
          <Grid item xs={12} sm={6} md={4} key={stat.label}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, background: theme.palette.background.paper, boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>{stat.label}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary, mt: 0.5 }}>{stat.value}</Typography>
              </Box>
              <Box sx={{ color: stat.color }}>{stat.icon}</Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, background: theme.palette.background.paper, boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)', p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                placeholder="Search by phone number"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                inputRef={searchInputRef}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: theme.palette.text.secondary }} /></InputAdornment>), endAdornment: searchInput && (<InputAdornment position="end"><IconButton size="small" onClick={handleClearSearch} edge="end"><ClearIcon fontSize="small" /></IconButton></InputAdornment>), sx: { backgroundColor: isDark ? theme.palette.background.default : '#fff', '&:hover': { backgroundColor: isDark ? theme.palette.background.default : '#fff' }, pr: 0.5 } }}
                size="small"
                sx={{ flex: 1, minWidth: 0 }}
              />
              <Button variant="contained" onClick={handleSearchClick} startIcon={<SearchIcon />} sx={{ minWidth: { xs: 64, sm: '100px' }, flexShrink: 0, height: 40 }}>Search</Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
              {isAdmin && (
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 }, width: { xs: '100%', sm: 'auto' } }}>
                  <InputLabel>Filter by Business</InputLabel>
                  <Select
                    value={businessIdFilter}
                    label="Filter by Business"
                    onChange={handleBusinessIdChange}
                    displayEmpty
                    disabled={businessOptionsLoading}
                    startAdornment={(<InputAdornment position="start"><BusinessCenterIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} /></InputAdornment>)}
                    endAdornment={businessIdFilter && (
                      <InputAdornment position="end" sx={{ mr: 2 }}>
                        <IconButton
                          size="small"
                          edge="end"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); handleClearBusinessId(); }}
                        >
                          <ClearIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    )}
                    sx={{ backgroundColor: isDark ? theme.palette.background.default : '#fff' }}
                  >
                    <MenuItem value="">All Businesses</MenuItem>
                    {businessOptions.map((b) => (
                      <MenuItem key={b.businessId} value={b.businessId}>{b.businessName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel>Filter by Status</InputLabel>
                <Select value={filterBlocked} label="Filter by Status" onChange={(e) => { setFilterBlocked(e.target.value); setPage(0); }} sx={{ backgroundColor: isDark ? theme.palette.background.default : '#fff' }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active Only</MenuItem>
                  <MenuItem value="blocked">Blocked Only</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>

        {businessIdFilter && (<Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}><Chip label={`Business: ${selectedBusinessName || businessIdFilter}`} size="small" onDelete={handleClearBusinessId} deleteIcon={<ClearIcon />} sx={{ backgroundColor: alpha(theme.palette.warning.main, 0.16), color: isDark ? theme.palette.warning.light : theme.palette.warning.dark, fontWeight: 500 }} /></Box>)}

        {searchTerm && (<Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><Chip label={`Search: "${searchTerm}"`} size="small" onDelete={handleClearSearch} deleteIcon={<ClearIcon />} sx={{ backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1), color: theme.palette.primary.main, fontWeight: 500 }} /></Box>)}

        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Box sx={{ minWidth: 760 }}>
            <DataTable key={`${subscribers.length}-${page}-${refreshKey}`} columns={columns} data={subscribers} page={page} rowsPerPage={rowsPerPage} totalRows={totalRows} onPageChange={setPage} onRowsPerPageChange={setRowsPerPage} loading={loading} />
          </Box>
        </Box>
      </Paper>

      <Dialog open={!!blockDialog} onClose={() => { setBlockDialog(null); setBlockReason(''); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: theme.palette.primary.main, color: '#fff', fontWeight: 600 }}>Block Subscriber</DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.primary }}>Are you sure you want to block this subscriber?</Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: isDark ? theme.palette.background.default : '#f9fafb' }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}><strong>Phone:</strong> {blockDialog?.phoneNumber}</Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}><strong>Device ID:</strong> {blockDialog?.deviceId}</Typography>
          </Paper>
          <TextField autoFocus fullWidth label="Blocking Reason" placeholder="Enter reason for blocking (e.g., Stolen device, Suspicious activity)" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} multiline rows={3} required error={blockReason.trim() === ''} helperText={blockReason.trim() === '' ? 'Reason is required' : ''} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => { setBlockDialog(null); setBlockReason(''); }} variant="outlined">Cancel</Button>
          <Button onClick={handleBlock} variant="contained" color="error" disabled={!blockReason.trim() || blocking} sx={{ background: theme.palette.primary.main, '&:hover': { background: theme.palette.primary.dark } }}>{blocking ? 'Blocking...' : 'Block Subscriber'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!unblockDialog} onClose={() => { setUnblockDialog(null); setUnblockReason(''); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: theme.palette.primary.main, color: '#fff', fontWeight: 600 }}>Unblock Subscriber</DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.primary }}>Are you sure you want to unblock this subscriber?</Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: isDark ? theme.palette.background.default : '#f9fafb' }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}><strong>Phone:</strong> {unblockDialog?.phoneNumber}</Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}><strong>Device ID:</strong> {unblockDialog?.deviceId}</Typography>
            {unblockDialog?.blockReason && <Typography variant="body2" sx={{ mt: 1, color: theme.palette.error.main }}><strong>Block Reason:</strong> {unblockDialog.blockReason}</Typography>}
          </Paper>
          <TextField autoFocus fullWidth label="Unblocking Reason" placeholder="Enter reason for unblocking (e.g., Device recovered, False positive)" value={unblockReason} onChange={(e) => setUnblockReason(e.target.value)} multiline rows={3} required error={unblockReason.trim() === ''} helperText={unblockReason.trim() === '' ? 'Reason is required' : ''} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => { setUnblockDialog(null); setUnblockReason(''); }} variant="outlined">Cancel</Button>
          <Button onClick={handleUnblock} variant="contained" color="success" disabled={!unblockReason.trim() || unblocking} sx={{ background: theme.palette.primary.main, '&:hover': { background: theme.palette.primary.dark } }}>{unblocking ? 'Unblocking...' : 'Unblock Subscriber'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminSubscribers;